import { faDoorClosed, faPaperPlane, faPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useEffect, useRef, useState } from "react";
import { Button, Col, Form, InputGroup, Modal, Row } from "react-bootstrap";
import {
    ButtonRemover, ButtonTrash, JTable, JTBody, JTHead, JvCaption, MakeMessageError
} from "../../../../components/Components";
import { rgx_numberOnly } from "../../../../utils/utils";
import manifestoService from "../../../../services/ManifestoService";
import { FormularioInterface, ModalIncluirDFeProps, NotaInterface } from "../../../../interfaces";
import { useModalMessage } from "../../../../components/ModalMessage/useModalMessage";
import { useQuestion } from "../../../../components/ModalQuestion/useModalQuestion";
import { useModalProcessing } from "../../../../components/ModalProcessing/useModalProcessing";

const initialFieldsError: { cMunicipioCarrega: string[]; cMunicipioDescarca: string[]; chave: string[] } = {
    cMunicipioCarrega: [],
    cMunicipioDescarca: [],
    chave: [],
}

const initialState: FormularioInterface = {
    cMunCarrega: 1,
    infDoc: []
}

const initialNotaState: NotaInterface = {
    chNFe: '',
    cMunDescarga: 1,
    xMunDescarga: '',
}

export const IncluirDFeForm: React.FC<ModalIncluirDFeProps> = ({ show, id, onHide }) => {
    const [errors, setErrors] = useState(initialFieldsError);
    const [form, setForm] = useState<FormularioInterface>(initialState)
    const [nota, setNota] = useState<NotaInterface>(initialNotaState);
    const [municipio, setMunicipio] = useState<number>(0);
    const [municipios, setMunicipios] = useState<any[]>([]);
    const cMunDescargaRef = useRef<HTMLSelectElement>(null);
    const btnEnviarRef = useRef<HTMLButtonElement>(null);
    const { alertModal, ModalMessageEl } = useModalMessage();
    const { confirm, ConfirmModal } = useQuestion();
    const { startProcessing, stopProcessing, ProcessingModalEl } = useModalProcessing();

    const validaPreenchimento = () => {
        if (nota.chNFe.trim().length !== 44) {
            setErrors({
                ...errors,
                chave: ['Tamanho da chave inválido. A chave de ter 44 caracteres.'],
            });

            return false;
        }

        if (nota.chNFe.substring(20, 22) !== '55') {
            setErrors({
                ...errors,
                chave: ['Modelo da chave inválido. Somente NF-e'],
            });

            return false;
        }

        if (form.infDoc.find(item => item.chNFe.trim() === nota.chNFe.trim())) {
            setErrors({
                ...errors,
                chave: ['Chave já foi adicionada'],
            });
            return false;
        }

        return true;
    }

    const handleAdd = () => {

        if (cMunDescargaRef.current?.value.trim() === "") {
            alertModal('Município de Carregamento e Descarga deve ser informado.');
            return;
        }

        const xMunDescarga = cMunDescargaRef.current?.options[cMunDescargaRef.current?.selectedIndex].text || '';

        if (validaPreenchimento()) {
            setForm({
                ...form,
                infDoc: [...form.infDoc,
                {
                    cMunDescarga: parseInt(nota.cMunDescarga.toString()),
                    xMunDescarga: xMunDescarga,
                    chNFe: nota.chNFe
                }
                ]
            })

            setNota({
                ...initialNotaState,
                cMunDescarga: municipio
            });
        }
    }

    const handleSend = async () => {
        if (form.infDoc.length === 0) {
            alertModal('Nenhuma chave informada. Adicione pelo menos uma chave.');
            return;
        }
        const confirmed = await confirm({
            title: "Confirmação",
            message: <>Enviar chave(s) ?</>,
            btnConfirmOptions: {
                title: 'Sim, Enviar',
                variant: 'success',
                icon: faPaperPlane
            }
        })
        if (confirmed) {
            btnEnviarRef.current?.setAttribute('disabled', 'disabled');
            startProcessing();
            await manifestoService.incluirDFe(id, form).then(res => {
                setForm({
                    ...initialState,
                    cMunCarrega: municipio
                })
                stopProcessing();
                alertModal("Chave(s) enviada(s) com sucesso", "success");
            }).catch(error => {
                stopProcessing();
                alertModal(error.response.data.message, "danger");
            })
            btnEnviarRef.current?.removeAttribute('disabled');
        }
    }

    const handleChange = (e: any) => {
        const { name, value } = e.target;
        setErrors(prev => ({ ...prev, [name]: "" }));
        setForm(prev => ({ ...prev, [name]: value }));
    }

    const handleChangeNota = (e: any) => {
        const { name, value } = e.target;

        setErrors(prev => ({ ...prev, [name]: "" }));

        const transformers: Record<string, (val: string) => string> = {
            chave: rgx_numberOnly,
        };

        const newValue = transformers[name] ? transformers[name](value) : value;
        setNota(prev => ({ ...prev, [name]: newValue }));
    }

    const handleRemover = async (chave: string) => {
        if (chave !== "") {
            const confirmed = await confirm({
                message: <>Remover <strong>{chave}</strong> ?</>,
                title: "Confirmação"
            });
            
            if (confirmed) {
                setForm({
                    ...form,
                    infDoc: [...form.infDoc.filter(item => item.chNFe.trim() !== chave.trim())]
                })
            }
        }
    }

    const handleRemoverTudo = async () => {
        if (form.infDoc.length > 0) {
            const confirmed = await confirm({
                message: <>Remover todas as chaves ?</>,
                title: "Confirmação"
            });
            if (confirmed) {
                setForm({
                    ...initialState,
                    cMunCarrega: municipio
                })
            }
        }
    }

    useEffect(() => {
        manifestoService.getDadosInclusaoDFe(id).then(res => {
            setMunicipios(res.municipios);

            setForm({
                ...form,
                cMunCarrega: res.cMunicipio
            });

            setNota({
                ...nota,
                cMunDescarga: res.cMunicipio
            });

            setMunicipio(res.cMunicipio);

        }).catch(error => {
            alertModal('Error ao carregar', "danger");
        })
    }, []);

    return (<>
        <Modal show={show} onHide={() => { onHide(); }} size="lg" centered backdrop='static'>
            <Modal.Header closeButton className="bg-success-subtle border-2 border-success py-2">
                <Modal.Title className="h6">Inclusão de DF-e</Modal.Title>
            </Modal.Header>

            <Modal.Body >
                <Row className="mb-3 g-2">
                    <Col md>
                        <Form.Group controlId="cMunCarrega">
                            <Form.Label>Município de Carregamento</Form.Label>
                            <Form.Select name="cMunCarrega"
                                value={form.cMunCarrega} disabled={form.infDoc.length > 0}
                                onChange={handleChange}>
                                {municipios.map((item: any) => (
                                    <option key={item.id} value={parseInt(item.cod_ibge.toString())}>
                                        {item.name}
                                    </option>
                                ))}
                            </Form.Select>
                            <MakeMessageError errors={errors} field="cMunCarrega" />
                        </Form.Group>
                    </Col>
                    <Col md>
                        <Form.Group controlId="cMunDescarga">
                            <Form.Label >Municipio de Descarga</Form.Label>
                            <Form.Select name="cMunDescarga" ref={cMunDescargaRef}
                                value={nota.cMunDescarga}
                                onChange={handleChangeNota}>
                                {municipios.map((item: any) => (
                                    <option key={item.id} value={parseInt(item.cod_ibge.toString())}>
                                        {item.name}
                                    </option>
                                ))}
                            </Form.Select>
                            <MakeMessageError errors={errors} field="cMunDescarga" />
                        </Form.Group>
                    </Col>
                </Row>

                <Row className="mb-3">
                    <Col md>
                        <Form.Group controlId="chNFe">
                            <Form.Label>Chave de acesso NF-e</Form.Label>
                            <InputGroup>
                                <Form.Control name="chNFe" maxLength={44}
                                    value={nota.chNFe}
                                    onChange={handleChangeNota} />
                                <Button onClick={handleAdd}>
                                    <FontAwesomeIcon icon={faPlus} />
                                </Button>
                            </InputGroup>
                            <MakeMessageError errors={errors} field="chNFe" />
                        </Form.Group>
                    </Col>
                </Row>

                <JTable className="caption-top mb-1 border border-2 border-success  table table-sm table-striped table-bordered">
                    <JvCaption className="text-center ps-2 fw-bold rounded-top-2 border-top border-start border-end  bg-success-subtle border-success border-2">Lista de Chaves a serem enviadas</JvCaption>
                    <JTHead>
                        <tr>
                            <th className="text-center" style={{ width: '2.5rem' }}>
                                <ButtonTrash onClick={() => handleRemoverTudo()} />
                            </th>
                            <th>Municipio de Descarga</th>
                            <th>Chave de acesso NF-e</th>
                        </tr>
                    </JTHead>
                    <JTBody>
                        {form.infDoc.map((item: NotaInterface) => (
                            <tr key={item.chNFe}>
                                <td className="text-center">
                                    <ButtonRemover onClick={() => handleRemover(item.chNFe)} />
                                </td>
                                <td>{item.xMunDescarga}</td>
                                <td>{item.chNFe}</td>
                            </tr>
                        ))}
                    </JTBody>
                </JTable>

            </Modal.Body>

            <Modal.Footer>
                <Button variant="success" onClick={handleSend}
                    disabled={form.infDoc.length === 0}
                    ref={btnEnviarRef}>
                    <FontAwesomeIcon icon={faPaperPlane} /> Enviar
                </Button>

                <Button variant="secondary" onClick={() => { onHide(); }}>
                    <FontAwesomeIcon icon={faDoorClosed} /> Fechar
                </Button>
            </Modal.Footer>
        </Modal>
        {ConfirmModal}
        {ModalMessageEl}
        {ProcessingModalEl}
    </>);
}